import axios, { AxiosError } from 'axios'
import { dialogs } from 'frontend/_globals/dialogs'
import { ToastAction } from 'frontend/_stores/toast-store'
import { getEventOrganizationUser } from 'frontend/common/access-helpers'
import { useEventAccess } from 'frontend/common/use-event-access'
import { DataModelActionType } from 'frontend/roles/enum/DataModelActionType'
import { computed, inject } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'

export function useCurrentEventActions() {
  const store = useStore()
  const route = useRoute()
  const { can } = useEventAccess()

  const showErrorToast: ToastAction = inject('$error')
  const showSuccessToast: ToastAction = inject('$success')

  const currentEvent = computed(() => {
    return store.getters['currentContext/currentEvent']
  })

  const slug = computed(() => {
    return route.params.eventSlug
  })

  const eventOrganizationUser = computed(() => {
    return getEventOrganizationUser(store.state?.session?.user, slug.value)
  })

  async function syncResource(action: string): Promise<void> {
    try {
      await store.axios({
        method: 'post',
        url: `events/${route.params.eventSlug}/${action}`,
      })
      showSuccessToast({ message: 'The action was scheduled, it is going to be performed soon' })
    } catch (error) {
      if (!axios.isAxiosError(error)) {
        throw error
      }
      const axiosError = error as AxiosError
      const toastPayload: {
        message: string
        title?: string
      } = { message: 'The action cannot be scheduled' }
      if (axiosError.response?.status == 422 && axiosError.response?.data?.errors?.base?.length) {
        toastPayload.title = toastPayload.message
        toastPayload.message =
          axiosError.response?.data.errors.base.constructor == Array
            ? axiosError.response?.data.errors.base.join('; ')
            : axiosError.response?.data.errors.base
      }
      showErrorToast(toastPayload)
    }
  }

  const currentEventActions = computed(() => {
    const result: {
      title?: string
      clickFn?: () => void
    }[] = []
    if (
      currentEvent.value?.syncLocationsEnabled &&
      can(DataModelActionType.SyncLocations, 'Event')
    ) {
      result.push({
        title: 'Sync Locations',
        clickFn: () => {
          dialogs.confirm().then(() => {
            syncResource('sync_locations')
          })
        },
      })
    }
    if (currentEvent.value?.syncClientsEnabled && can(DataModelActionType.SyncClients, 'Event')) {
      result.push({
        title: 'Sync Clients',
        clickFn: () => {
          dialogs.confirm().then(() => {
            syncResource('sync_clients')
          })
        },
      })
    }
    if (
      currentEvent.value?.syncClientCategoriesEnabled &&
      can(DataModelActionType.SyncClientCategories, 'Event')
    ) {
      result.push({
        title: 'Sync Client Group Paths',
        clickFn: () => {
          dialogs.confirm().then(() => {
            syncResource('sync_client_categories')
          })
        },
      })
    }
    if (
      currentEvent.value?.syncTransportBookingsEnabled &&
      can(DataModelActionType.SyncTransportBookings, 'Event')
    ) {
      result.push({
        title: 'Sync Transport Bookings',
        clickFn: () => {
          dialogs.confirm().then(() => {
            syncResource('sync_transport_bookings')
          })
        },
      })
    }
    if (
      currentEvent.value?.syncShuttleTransfersEnabled &&
      can(DataModelActionType.SyncShuttleTransfers, 'Event')
    ) {
      result.push({
        title: 'Sync Shuttle Transfers',
        clickFn: () => {
          dialogs.confirm().then(() => {
            syncResource('sync_shuttle_transfers')
          })
        },
      })
    }
    if (currentEvent.value?.syncDriversEnabled && can(DataModelActionType.SyncDrivers, 'Event')) {
      result.push({
        title: 'Sync Drivers',
        clickFn: () => {
          dialogs.confirm().then(() => {
            syncResource('sync_drivers')
          })
        },
      })
    }
    return result
  })

  const shouldShowEventDropdownActions = computed(() => {
    if (!currentEvent.value) return false
    if (!eventOrganizationUser.value) return false
    if (
      (!currentEvent.value.syncLocationsEnabled ||
        !can(DataModelActionType.SyncLocations, 'Event')) &&
      (!currentEvent.value.syncClientsEnabled || !can(DataModelActionType.SyncClients, 'Event')) &&
      (!currentEvent.value.syncClientCategoriesEnabled ||
        !can(DataModelActionType.SyncClientCategories, 'Event')) &&
      (!currentEvent.value.syncTransportBookingsEnabled ||
        !can(DataModelActionType.SyncTransportBookings, 'Event')) &&
      (!currentEvent.value.syncShuttleTransfersEnabled ||
        !can(DataModelActionType.SyncShuttleTransfers, 'Event')) &&
      (!currentEvent.value.syncDriversEnabled || !can(DataModelActionType.SyncDrivers, 'Event'))
    )
      return false
    return true
  })

  return {
    currentEventActions,
    shouldShowEventDropdownActions,
  }
}
